import React, { useEffect, useState } from 'react';
import KonuSelect from './KonuSelect';
import TimeField24 from './TimeField24';
import { AutoMark, ManualMark } from './GoalMarks';
import {
  GOAL_DATE_LABEL, GOAL_KONU_HINT, GOAL_PERIOD_OPTIONS, GOAL_TITLE_MAX,
  formatGoalWindow, goalAutoNote, goalTrackingOptions,
  type GoalFormRole, type GoalFormValue,
} from '../utils/goalForm';

/**
 * The goal form's fields — one layout for the student's own "Yeni Hedef" and
 * the teacher's "Hedef Ata" (one student or a class). The mobile twin is
 * dijipo-mobile/components/goals/GoalFormFields.tsx; the rules both follow are
 * in utils/goalForm.ts.
 *
 * Order follows the decisions a person makes: name it, how long it runs (the
 * picked segment shows the actual dates), when on the day (daily only), what
 * it covers, how many, and what counts toward it.
 */
interface Props {
  role: GoalFormRole;
  value: GoalFormValue;
  onChange: (patch: Partial<GoalFormValue>) => void;
  subjects: Array<{ id: number; name: string }>;
  /** Hint under an empty ders list (the teacher's comes from their branch's books). */
  noSubjectsHint?: string;
  loadKonular: (subjectId: number) => Promise<Array<{ id: number; title: string }>>;
}

const F: React.CSSProperties = { fontFamily: 'Inter' };
const label = 'block text-[13px] font-semibold text-[#2b2b2b] mb-1.5';
const field = 'w-full bg-white rounded-[12px] px-3 h-[44px] outline-none text-[14px] border border-[#cfd8e6] focus:border-[#3973bf] focus-visible:ring-2 focus-visible:ring-[#3973bf]/25';
const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3973bf]';

const GoalFormFields: React.FC<Props> = ({ role, value, onChange, subjects, noSubjectsHint, loadKonular }) => {
  const [konular, setKonular] = useState<Array<{ id: number; title: string }>>([]);
  const [konuLoading, setKonuLoading] = useState(false);

  // Konular follow the ders; a late answer for a ders already left is dropped.
  useEffect(() => {
    if (value.subjectId == null) { setKonular([]); setKonuLoading(false); return; }
    let alive = true;
    setKonular([]);
    setKonuLoading(true);
    loadKonular(value.subjectId)
      .then((list) => { if (alive) setKonular(list); })
      .catch(() => { if (alive) setKonular([]); })
      .finally(() => { if (alive) setKonuLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.subjectId]);

  const tracking = goalTrackingOptions(role);

  return (
    <div style={F} className="space-y-4">
      <div>
        <label htmlFor="goal-title" className={label}>Hedef adı</label>
        <input
          id="goal-title"
          value={value.title}
          maxLength={GOAL_TITLE_MAX}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Örn. Türev tekrarı"
          autoFocus
          className={field}
        />
      </div>

      <div>
        <span id="goal-period" className={label}>Ne kadar sürecek?</span>
        <div role="radiogroup" aria-labelledby="goal-period" className="grid grid-cols-3 gap-2">
          {GOAL_PERIOD_OPTIONS.map((o) => {
            const sel = value.period === o.value;
            // The picked segment says which dates that actually is.
            const caption = sel ? (formatGoalWindow(o.value, value.date) ?? o.hint) : o.hint;
            return (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={sel}
                onClick={() => onChange({ period: o.value })}
                className={`rounded-[12px] px-3 py-2.5 text-left transition-colors ${focusRing} ${sel ? 'bg-[#3973bf] text-white' : 'bg-white text-[#2b2b2b] border border-[#cfd8e6] hover:border-[#3973bf]'}`}
              >
                <span className="block text-[14px] font-bold leading-tight">{o.label}</span>
                <span className={`block text-[12px] mt-0.5 leading-tight tabular-nums ${sel ? 'text-white/90' : 'text-gray-500'}`}>{caption}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label htmlFor="goal-date" className={label}>{GOAL_DATE_LABEL[value.period]}</label>
        {/* One type="date" for all three periods: week/month inputs don't exist in
            Safari or Firefox. The resolved window is in the segment above. */}
        <input id="goal-date" type="date" value={value.date} onChange={(e) => onChange({ date: e.target.value })} className={field} />
      </div>

      {value.period === 'daily' && (
        <div>
          <span id="goal-time" className={label}>Gün içinde</span>
          <div className="flex items-center gap-2 flex-wrap">
            <div role="radiogroup" aria-labelledby="goal-time" className="inline-flex rounded-[12px] bg-white border border-[#cfd8e6] p-1">
              {([['Tüm gün', true], ['Saat aralığı', false]] as const).map(([text, whole]) => (
                <button
                  key={text}
                  type="button"
                  role="radio"
                  aria-checked={value.wholeDay === whole}
                  onClick={() => onChange({ wholeDay: whole })}
                  className={`px-3 h-[34px] rounded-[9px] text-[13px] font-semibold transition-colors ${focusRing} ${value.wholeDay === whole ? 'bg-[#3973bf] text-white' : 'text-[#2b2b2b] hover:bg-[#f0f5fc]'}`}
                >{text}</button>
              ))}
            </div>
            {!value.wholeDay && (
              <div className="flex items-center gap-2">
                <TimeField24 aria-label="Başlangıç saati" value={value.startTime} onChange={(v) => onChange({ startTime: v })} />
                <span className="text-gray-500" aria-hidden>–</span>
                <TimeField24 aria-label="Bitiş saati" value={value.endTime} onChange={(v) => onChange({ endTime: v })} />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="goal-subject" className={label}>Ders</label>
          <select
            id="goal-subject"
            value={value.subjectId ?? ''}
            onChange={(e) => onChange({ subjectId: e.target.value ? Number(e.target.value) : null, subjectUnitId: null })}
            disabled={subjects.length === 0}
            className={`${field} disabled:opacity-60`}
          >
            <option value="">{subjects.length === 0 ? 'Ders bulunamadı' : 'Tüm dersler'}</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {subjects.length === 0 && noSubjectsHint && <p className="text-[11px] text-gray-500 mt-1">{noSubjectsHint}</p>}
        </div>
        <div>
          <span className={label}>Konu</span>
          <KonuSelect
            value={value.subjectUnitId ?? ''}
            onChange={(v) => onChange({ subjectUnitId: v === '' ? null : Number(v) })}
            options={konular}
            loading={konuLoading}
            disabled={value.subjectId == null || (!konuLoading && konular.length === 0)}
            placeholder={value.subjectId == null ? 'Önce ders seçin' : 'Konu seçilmedi'}
            borderColor="#cfd8e6"
          />
          {value.subjectUnitId != null && value.tracking !== 'manual' && (
            <p className="text-[11px] text-gray-500 mt-1">{GOAL_KONU_HINT}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="goal-target" className={label}>Soru sayısı</label>
        <input
          id="goal-target"
          type="number"
          inputMode="numeric"
          min={1}
          max={5000}
          value={value.target}
          onChange={(e) => onChange({ target: e.target.value })}
          className={`${field} max-w-[160px] font-semibold tabular-nums`}
        />
      </div>

      <div>
        <span id="goal-tracking" className={label}>Nasıl sayılsın?</span>
        <div role="radiogroup" aria-labelledby="goal-tracking" className="space-y-2">
          {tracking.map((o) => {
            const sel = value.tracking === o.value;
            return (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={sel}
                onClick={() => onChange({ tracking: o.value })}
                className={`w-full flex items-start gap-3 rounded-[12px] px-3 py-2.5 text-left border-2 transition-colors ${focusRing} ${sel ? 'border-[#3973bf] bg-[#f0f5fc]' : 'border-transparent bg-white hover:border-[#cfd8e6]'}`}
              >
                <span className="mt-0.5 flex items-center gap-0.5 w-[32px]" aria-hidden>
                  {o.auto && <AutoMark />}
                  {o.manual && <ManualMark />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-semibold text-[#2b2b2b]">{o.label}</span>
                  <span className="block text-[12px] text-gray-500 leading-snug">{o.detail}</span>
                </span>
                <span className={`mt-1 w-4 h-4 rounded-full border-2 flex-shrink-0 ${sel ? 'border-[#3973bf] bg-[#3973bf] shadow-[inset_0_0_0_3px_#fff]' : 'border-[#c5c5c5] bg-white'}`} aria-hidden />
              </button>
            );
          })}
        </div>
        <p className="text-[12px] text-gray-500 mt-2 flex items-center gap-1"><AutoMark size={12} /> {goalAutoNote(role)}</p>
      </div>
    </div>
  );
};

export default GoalFormFields;
